import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { NgModel } from '@angular/forms';
import { Profile } from 'src/app/general-components/profile/profile';
import { AuthService } from 'src/app/auth/auth.service';
import { EmptyKrampSpecial, KrampSpecial } from '../kramp-special';
import { ReservedSpecialSku } from '../../reserved-special-sku';
import { EmptyStats, Stats, SupplierWithStats } from '../../suppliers/supplier';
import { KrampSpecialCode } from './kramp-special-code';
import { KRAMP_SPECIAL_CODES } from './kramp-special-codes';

@Component({
  selector: 'app-new-kramp-special',
  templateUrl: './new-kramp-special.component.html',
  styleUrls: ['./new-kramp-special.component.scss'],
})
export class NewKrampSpecialComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() suppliers: SupplierWithStats[] = [];

  @Input() reservedSku: ReservedSpecialSku = { id: 0, sku: '' };
  @Output() keepInputs = new EventEmitter<boolean>();
  @Output() createNewSpecial = new EventEmitter<KrampSpecial>();

  @ViewChild('description') description!: NgModel;
  @ViewChild('specialCode') specialCode!: NgModel;
  @ViewChild('supplier') supplier!: NgModel;
  @ViewChild('supplierSku') supplierSku!: NgModel;
  @ViewChild('purchasePrice') purchasePrice!: NgModel;
  @ViewChild('supplierPrice') supplierPrice!: NgModel;
  @ViewChild('salesPrice') salesPrice!: NgModel;
  @ViewChild('orderUnit') orderUnit!: NgModel;
  @ViewChild('deliveryDays') deliveryDays!: NgModel;

  selectedSupplierStats: Stats = EmptyStats();

  readonly supplierSKUPattern: string = '^[a-zA-Z0-9 :+=#\\u00D8,.&\\-\\/\\(\\)"]*$';

  showForm = false;

  newSpecial: KrampSpecial = EmptyKrampSpecial();
  profile!: Profile;

  margin: number | undefined;
  specialCodes: KrampSpecialCode[] = KRAMP_SPECIAL_CODES;
  searchTextSpecialCode: string = '';
  searchTextSupplier: string = '';

  private viewInitialized = false;

  constructor(private authService: AuthService) {}

  ngOnInit(): void {
    this.newSpecial.sku = this.reservedSku.sku;
    this.newSpecial.id = this.reservedSku.id;
    this.profile = this.authService.getUserInfo();
    this.syncSupplierSearchText();
  }

  ngOnChanges(): void {
    this.syncSupplierSearchText();
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
  }

  retryFailedSpecial(failedSpecial: KrampSpecial) {
    this.newSpecial = failedSpecial;
    this.syncSupplierSearchText();
  }

  toggleNewSpecial() {
    this.showForm = !this.showForm;
  }

  updateSpecialCode(searchString: string) {
    const results = this.specialCodes.filter(
      specialCode => specialCode.code.includes(searchString) || specialCode.name.includes(searchString)
    );
    if (results.length == 1) {
      this.newSpecial.krampSpecialCode = results[0].code;
    } else {
      this.newSpecial.krampSpecialCode = searchString;
    }
  }

  updatePricing() {
    this.newSpecial.supplierPrice = this.newSpecial.purchasePrice;
    this.updateSalesPrice();

    if (this.margin != undefined && this.newSpecial.supplierPrice != 0 && this.newSpecial.salesPrice != 0) {
      this.updateMargin();
    }
  }

  updateMargin() {
    if (this.newSpecial.supplierPrice != 0 && this.newSpecial.salesPrice != 0) {
      const diff = this.newSpecial.salesPrice - this.newSpecial.supplierPrice;
      const margin = (diff / this.newSpecial.salesPrice) * 100;
      this.margin = Number(margin.toFixed(2));
    }
  }

  updateSalesPrice() {
    if (this.margin != undefined && this.newSpecial.supplierPrice != 0) {
      const formattedMargin = (100 - this.margin) / 100;
      const newSalesPrice = this.newSpecial.supplierPrice / formattedMargin;
      this.newSpecial.salesPrice = Number(newSalesPrice.toFixed(4));
    }
  }

  toFixed(value: number, type: string) {
    switch (type) {
      case 'purchasePrice':
        this.newSpecial.purchasePrice = Number(value.toFixed(4));
        this.updatePricing();
        break;
      case 'supplierPrice':
        this.newSpecial.supplierPrice = Number(value.toFixed(4));
        this.updateMargin();
        break;
      case 'salesPrice':
        this.newSpecial.salesPrice = Number(value.toFixed(4));
        this.updateMargin();
        break;
      default:
        break;
    }
  }

  isFormValid(): boolean {
    if (!this.viewInitialized) {
      return false;
    }
    return Boolean(
      this.description.valid &&
        this.newSpecial.krampSpecialCode != '' &&
        this.specialCode.valid &&
        this.newSpecial.krampSupplierId != -1 &&
        this.isSupplierSelectionValid() &&
        this.supplierSku.valid &&
        this.purchasePrice.valid &&
        this.supplierPrice.valid &&
        this.salesPrice.valid &&
        this.margin &&
        this.margin >= 0 &&
        this.margin <= 99.99 &&
        this.orderUnit.valid &&
        this.deliveryDays.valid
    );
  }

  createSpecial(special: KrampSpecial, keepInputs: boolean) {
    special.id = this.reservedSku.id;
    special.sku = this.reservedSku.sku;
    special.creationBy = this.profile.fullName;
    this.keepInputs.emit(keepInputs);
    this.createNewSpecial.emit(this.cleanUpSpecial(special));
  }

  clearSpecial() {
    this.newSpecial = EmptyKrampSpecial();
    this.description.reset();
    this.specialCode.reset();
    this.supplier.reset();
    this.supplierSku.reset();
    this.purchasePrice.reset();
    this.supplierPrice.reset();
    this.salesPrice.reset();
    this.searchTextSupplier = '';
    this.selectedSupplierStats = EmptyStats();
  }

  clearUniqueFields() {
    this.description.reset();
  }

  cleanUpSpecial(special: KrampSpecial): KrampSpecial {
    special.description = special.description.trim();
    special.krampSupplierSku = special.krampSupplierSku.trim();
    return special;
  }

  updateSupplierStats(supplierId: number): void {
    const supplier = this.suppliers.find(s => s.supplierId == supplierId);
    this.selectedSupplierStats = supplier?.stats ?? EmptyStats();
  }

  isSupplierSelectionValid(): boolean {
    return this.newSpecial.krampSupplierId != -1;
  }

  updateSupplierSelection(searchValue: string): void {
    const trimmedValue = searchValue.trim();
    if (!trimmedValue) {
      this.newSpecial.krampSupplierId = -1;
      this.selectedSupplierStats = EmptyStats();
      return;
    }

    const matchedSupplier = this.findSupplierFromInput(trimmedValue);
    if (matchedSupplier) {
      this.newSpecial.krampSupplierId = matchedSupplier.supplierId;
      this.searchTextSupplier = this.formatSupplierDisplay(matchedSupplier);
      this.updateSupplierStats(matchedSupplier.supplierId);
      return;
    }

    this.newSpecial.krampSupplierId = -1;
    this.selectedSupplierStats = EmptyStats();
  }

  formatSupplierDisplay(supplier: { name: string; supplierId: number }): string {
    return `${supplier.name} (${String(supplier.supplierId)})`;
  }

  private findSupplierFromInput(input: string): SupplierWithStats | undefined {
    const normalizedInput = input.toLowerCase();
    const exactDisplayMatch = this.suppliers.find(
      supplier => this.formatSupplierDisplay(supplier).toLowerCase() === normalizedInput
    );
    if (exactDisplayMatch) {
      return exactDisplayMatch;
    }

    const exactNameMatch = this.suppliers.find(supplier => supplier.name.toLowerCase() === normalizedInput);
    if (exactNameMatch) {
      return exactNameMatch;
    }

    if (/^\d+$/.test(input)) {
      const supplierId = Number(input);
      const exactIdMatch = this.suppliers.find(supplier => supplier.supplierId === supplierId);
      if (exactIdMatch) {
        return exactIdMatch;
      }
    }

    const partialMatches = this.suppliers.filter(
      supplier =>
        supplier.name.toLowerCase().includes(normalizedInput) ||
        String(supplier.supplierId).toLowerCase().includes(normalizedInput)
    );
    if (partialMatches.length === 1) {
      return partialMatches[0];
    }

    return undefined;
  }

  private syncSupplierSearchText(): void {
    if (!this.suppliers.length) {
      return;
    }

    const supplier = this.suppliers.find(s => s.supplierId == this.newSpecial.krampSupplierId);
    if (supplier) {
      this.searchTextSupplier = this.formatSupplierDisplay(supplier);
      this.selectedSupplierStats = supplier.stats;
      return;
    }

    if (this.newSpecial.krampSupplierId == -1 && !this.searchTextSupplier) {
      this.selectedSupplierStats = EmptyStats();
    }
  }
}
